import React from 'react';
import styled, { keyframes } from 'styled-components';

interface AudioWaveformProps {
  isRecording: boolean;
  audioLevel: number;
  isTranscribing?: boolean;
}

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
`;

const WaveformContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isRecording'
})<{ isRecording: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  opacity: ${props => props.isRecording ? 1 : 0.5};
  transition: opacity 0.3s ease;
`;

const WaveBar = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'height' && prop !== 'isRecording' && prop !== 'delay'
}).attrs<{ 
  height: number; 
  isRecording: boolean;
  delay: number;
}>(({ height, isRecording, delay }) => ({
  style: {
    height: `${height}px`,
    animationDelay: `${delay}ms`,
    animation: isRecording ? 'pulse 1.5s infinite' : 'none'
  }
}))`
  width: 3px;
  background: linear-gradient(135deg, #ff69b4, #ffb6c1);
  border-radius: 2px;
  transition: height 0.1s ease;
`;

const TranscribingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(139, 69, 19, 0.7);
  font-size: 0.9rem;
  
  .dots {
    display: flex;
    gap: 2px;
  }
  
  .dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #ff69b4;
    animation: ${pulse} 1.4s infinite ease-in-out;
    
    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
`;

export const AudioWaveform: React.FC<AudioWaveformProps> = ({ 
  isRecording, 
  audioLevel: _audioLevel, 
  isTranscribing = false 
}) => {
  // Generate simple waveform bars
  const generateBars = () => {
    const bars = [];
    const barCount = 12;
    const baseHeight = 6;
    const maxHeight = 16;
    
    for (let i = 0; i < barCount; i++) {
      // Simple pattern: alternating heights with slight variation
      const isEven = i % 2 === 0;
      const height = isEven ? baseHeight : maxHeight;
      const slightVariation = Math.random() * 2; // ±1px variation
      const finalHeight = height + slightVariation;
      
      bars.push(
        <WaveBar
          key={i}
          height={Math.max(finalHeight, 3)}
          isRecording={isRecording}
          delay={i * 30}
        />
      );
    }
    
    return bars;
  };

  if (isTranscribing) {
    return (
      <TranscribingIndicator>
        Transcribing
        <div className="dots">
          <div className="dot"></div>
          <div className="dot"></div>
          <div className="dot"></div>
        </div>
      </TranscribingIndicator>
    );
  }

  return (
    <WaveformContainer isRecording={isRecording}>
      {generateBars()}
    </WaveformContainer>
  );
};
